import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification, NotificationKind, NotificationList } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-14 — NOTIFICATIONS INBOX. TanStack Query over the generated client, mirroring data/reviews.ts
 * (unwrap, body `as never`, onSuccess invalidate the whole tree).
 *
 * The notifications inbox is a SALON-LEVEL ops surface with SALON-WIDE shared read-state: read_at is a
 * salon fact, not a per-user one, so a mark-read by ANY session is something every other session must
 * re-read. We therefore invalidate the whole `['notifications']` prefix on every write so each filtered
 * view AND the unread badge re-read from the server together.
 *
 * Near-live: the list polls (refetchInterval) and stays briefly fresh (a small staleTime) so the badge
 * tracks new low-stock / online-booking / comms-at-cap / no-show events without a manual refresh — but
 * it is an operational poll, never a money read, and it is NOT mirrored offline (see lib/query.ts).
 *
 * PII discipline lives on the API: a notification's title/body never carries a sealed renter's client
 * identity (service/time/staff/product only). The console only DISPLAYS those denormalised strings; it
 * never joins back to the clients table.
 *
 * RBAC: the list read + both mark-read writes are allowed for any staff role (RLS-scoped on the API).
 * There is no create surface — notifications are minted only internally by the job handler.
 */

const POLL_MS = 60_000;

export interface NotificationsFilter {
  unreadOnly?: boolean;
  kind?: NotificationKind | null;
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * The salon notifications list (newest-first on the API) + the salon-wide unreadCount and the total
 * matching count. Optionally filtered to unread-only and/or a single kind. Polls every ~60s and carries
 * a small staleTime so the badge is near-live; the list endpoint returns 200 with an empty page when
 * there is nothing, so there is no 404 to map.
 */
export function useNotifications(filter: NotificationsFilter = {}) {
  const unreadOnly = filter.unreadOnly ?? false;
  const kind = filter.kind ?? null;
  return useQuery({
    queryKey: qk.notifications({ unreadOnly, kind }),
    staleTime: 15_000,
    refetchInterval: POLL_MS,
    queryFn: async (): Promise<NotificationList> =>
      unwrap(
        await api.GET('/v1/notifications', {
          params: {
            query: {
              ...(unreadOnly ? { unreadOnly: true } : {}),
              ...(kind ? { kind } : {}),
            },
          },
        }),
      ),
  });
}

// ── Mutations (any staff role) ───────────────────────────────────────────────────────

/**
 * Mark ONE notification read (idempotent on the API — a re-mark is a safe no-op returning the row).
 * read_at is salon-wide, so invalidate the whole tree: every filtered view and the badge re-read.
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Notification> =>
      unwrap(await api.POST('/v1/notifications/{id}/read', { params: { path: { id } } })),
    onSuccess: () => invalidateNotifications(qc),
  });
}

/**
 * Mark ALL the salon's notifications read (idempotent). Returns the count updated. Invalidates the whole
 * tree so the unread badge clears everywhere.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ updated: number }> =>
      unwrap(await api.POST('/v1/notifications/read-all', {})),
    onSuccess: () => invalidateNotifications(qc),
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────

/**
 * Any mark-read shifts the salon-wide read-state, so every notifications view (each unread/kind filter)
 * AND the unread badge re-read together. One prefix covers them all — the keys are
 * `['notifications', { unreadOnly, kind }]`.
 */
function invalidateNotifications(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['notifications'] });
}
